import { gql, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import type { Organization } from '@interfaces/organization';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { useTranslation } from 'react-i18next';

const FETCH_PUBLIC_ORGANIZATIONS = gql`
  query fetchPublicOrganizations($input: FetchPublicOrganizationsInput!) {
    fetchPublicOrganizations(input: $input) {
      organizations {
        id
        subdomain
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        theme {
          authenticationTheme {
            logo
            primaryColor
            pageBackgroundColor
          }
        }
      }
      totalCount
    }
  }
`;

export const PublicOrganizationSelectDialog = ({
  filter,
  open,
  onOrgSelect,
}: {
  filter?: {
    childrenFor?: string;
    searchText?: string;
    referenceIds?: string[];
  };
  open: boolean;
  onOrgSelect: (organization: Organization) => void;
}) => {
  const { t } = useTranslation('authentication');
  const theme = useTheme();
  const { data, loading } = useQuery(FETCH_PUBLIC_ORGANIZATIONS, {
    variables: {
      input: {
        filter,
      },
    },
  });
  const OrgAvatar = (props: {
    name?: string;
    logo?: string;
    fgColor?: string;
    bgColor?: string;
  }) => {
    if (!props.logo && !props.name) return <></>;
    return (
      <Avatar
        selected
        size='small'
        sx={{
          color: props.fgColor || theme.palette.supportOne60,
          bgcolor: props.bgColor || theme.palette.supportOne10,
        }}
      >
        {props.logo ? (
          <img alt='org-logo' width='100%' height='auto' src={props.logo} />
        ) : (
          props?.name?.[0]?.toUpperCase()
        )}
      </Avatar>
    );
  };

  return (
    <Dialog open={open} fullWidth maxWidth='xs'>
      <DialogTitle
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Grid container>
          <Grid
            size={{ md: 12 }}
            sx={{
              justifyContent: 'space-between',
              display: 'flex',
            }}
          >
            <Typography variant='titleLarge'>
              {t('publicOrganizationSelectDialog.title')}
            </Typography>
          </Grid>
          <Grid size={{ md: 12 }}>
            <Typography variant='bodyLarge' sx={{ mt: 3 }}>
              {t('publicOrganizationSelectDialog.subTitle')}
            </Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {!loading &&
          (data?.fetchPublicOrganizations?.organizations || []).map(
            (org: Organization) => (
              <Box
                key={org.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginBottom: 2.5,
                }}
              >
                <MenuItem
                  key={org.id}
                  sx={{
                    pv: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                  onClick={() => {
                    onOrgSelect(org);
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Box component='span' sx={{ mr: 1.5 }}>
                      {
                        <OrgAvatar
                          name={translateBackend(org.translatedName)}
                          logo={org.theme?.authenticationTheme?.logo}
                          fgColor={org.theme?.authenticationTheme?.primaryColor}
                          bgColor={
                            org.theme?.authenticationTheme?.pageBackgroundColor
                          }
                        />
                      }
                    </Box>
                    <Typography
                      variant='bodyLarge'
                      sx={{
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: '1',
                        WebkitBoxOrient: 'vertical',
                        marginRight: 1,
                      }}
                    >
                      {translateBackend(org.translatedName)}
                    </Typography>
                  </Box>
                </MenuItem>
              </Box>
            ),
          )}
      </DialogContent>
    </Dialog>
  );
};
